import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Save, FileText } from "lucide-react";

const input = "mt-1.5 w-full bg-black border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200";
const label = "text-[10px] uppercase tracking-widest text-zinc-500";

export default function AdminContent() {
  const [c, setC] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setC(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const toggleSamples = async (show) => {
    try {
      const { data } = await api.put("/admin/settings", { show_samples: show });
      setSettings(data);
      toast.success(show ? "Sample listings are visible again" : "Sample listings hidden from the public site");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const purgeSamples = async () => {
    if (!window.confirm("Permanently delete all sample coaches, their camps and demo bookings? This cannot be undone.")) return;
    try {
      const { data } = await api.delete("/admin/samples");
      toast.success(`Deleted ${data.deleted_coaches} sample coaches and ${data.deleted_camps} demo camp(s)`);
      const { data: s } = await api.get("/admin/settings");
      setSettings(s);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetData = async () => {
    if (!window.confirm("Delete ALL example coaches, demo camps, test bookings, payments and reviews? Insights will reset to zero. This cannot be undone.")) return;
    try {
      const { data } = await api.delete("/admin/reset-data?confirm=WIPE");
      toast.success(`Reset complete — removed ${data.coaches} example coaches and ${data.bookings} test bookings`);
      const { data: s } = await api.get("/admin/settings");
      setSettings(s);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/content", c);
      setC(data);
      toast.success("Site content updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!c) return <div className="p-20 text-center text-zinc-500">Loading content…</div>;

  const setAbout = (k, v) => setC({ ...c, about: { ...c.about, [k]: v } });
  const setSection = (s, k, v) => setC({ ...c, [s]: { ...c[s], [k]: v } });

  const legal = [
    { key: "disclosure", name: "Affiliate & commission disclosure", note: "Required in Canada before you promote the site or use tracking links." },
    { key: "privacy", name: "Privacy policy", note: "Required because you collect names and emails at sign-up." },
    { key: "terms", name: "Booking & cancellation terms", note: "Shown to families so refund rules are never a surprise." },
  ];

  return (
    <div data-testid="admin-content-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <span className="text-[11px] uppercase tracking-[0.3em] text-[#ff3b30]">Admin</span>
      <h1 className="display text-4xl sm:text-5xl uppercase tracking-tighter font-bold mt-3">Site content</h1>
      <p className="text-sm text-zinc-400 mt-4 max-w-xl leading-relaxed">Everything here appears on your public About and legal pages. Blank fields fall back to the built-in defaults.</p>

      {settings && (
        <div data-testid="sample-data-panel" className="mt-8 border border-[#ff3b30]/40 bg-[#121212] p-6 max-w-3xl">
          <h2 className="display text-xl uppercase tracking-tight font-bold">Sample listings</h2>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
            Your site was seeded with <strong>{settings.sample_coaches}</strong> example coaches (and their demo camp) so
            you could see how it works. Hide them so only real coaches you've approved appear to the public — this takes
            effect immediately, no redeploy needed.
          </p>
          {settings.samples_purged && (
            <p data-testid="samples-purged-note" className="text-[11px] uppercase tracking-widest text-[#00e5ff] mt-3">
              example &amp; test data deleted — nothing will be re-created
            </p>
          )}
          <div className={`flex flex-wrap items-center gap-3 mt-5 ${settings.samples_purged ? "hidden" : ""}`}>
            <button data-testid="toggle-samples-btn" onClick={() => toggleSamples(!settings.show_samples)}
              className={`text-[11px] uppercase tracking-widest px-5 py-3 border transition-colors duration-200 ${
                settings.show_samples ? "border-[#ff3b30] text-[#ff3b30] hover:bg-[#ff3b30] hover:text-white"
                                      : "border-[#00e5ff] text-[#00e5ff] hover:bg-[#00e5ff] hover:text-black"}`}>
              {settings.show_samples ? "Hide sample listings" : "Show sample listings"}
            </button>
            <span data-testid="samples-status" className="text-[11px] uppercase tracking-widest text-zinc-500">
              currently {settings.show_samples ? "visible to the public" : "hidden from the public"}
            </span>
            {settings.sample_coaches > 0 && (
              <button data-testid="purge-samples-btn" onClick={purgeSamples}
                className="text-[11px] uppercase tracking-widest border border-white/15 text-zinc-400 px-5 py-3 hover:border-[#ff3b30] hover:text-[#ff3b30] transition-colors duration-200">
                Delete them permanently
              </button>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-white/10">
            <h3 className="display text-sm uppercase tracking-widest font-bold">Wipe all test &amp; example data</h3>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
              Removes every example coach and demo camp plus all test bookings, payments, reviews and payout batches, so
              your Admin Insights start from zero. Sample data will not be re-created afterwards.
            </p>
            <p data-testid="reset-data-counts" className="text-[11px] uppercase tracking-widest text-zinc-500 mt-3">
              {settings.sample_coaches} example coaches · {settings.camps} camps · {settings.bookings} bookings · {settings.reviews} reviews · {settings.payout_batches} payout batches
            </p>
            <button data-testid="reset-data-btn" onClick={resetData}
              className="mt-4 text-[11px] uppercase tracking-widest px-5 py-3 border border-[#ff3b30] text-[#ff3b30] hover:bg-[#ff3b30] hover:text-white transition-colors duration-200">
              Reset all test &amp; example data
            </button>
          </div>
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-white/10 bg-[#121212] p-6 space-y-4">
          <h2 className="display text-xl uppercase tracking-tight font-bold">About page</h2>
          <label className="block"><span className={label}>Page heading</span>
            <input data-testid="content-heading-input" className={input} value={c.about.heading} onChange={(e) => setAbout("heading", e.target.value)} /></label>
          <label className="block"><span className={label}>Intro paragraph</span>
            <textarea data-testid="content-intro-input" rows={4} className={input} value={c.about.intro} onChange={(e) => setAbout("intro", e.target.value)} /></label>
          <label className="block"><span className={label}>Mission (one sentence)</span>
            <textarea data-testid="content-mission-input" rows={2} className={input} value={c.about.mission} onChange={(e) => setAbout("mission", e.target.value)} /></label>
          <label className="block"><span className={label}>Values (comma separated)</span>
            <input data-testid="content-values-input" className={input} value={(c.about.values || []).join(", ")}
              onChange={(e) => setAbout("values", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block"><span className={label}>Contact email</span>
              <input data-testid="content-contact-input" className={input} value={c.about.contact_email} onChange={(e) => setAbout("contact_email", e.target.value)} /></label>
            <label className="block"><span className={label}>Location</span>
              <input data-testid="content-location-input" className={input} value={c.about.location} onChange={(e) => setAbout("location", e.target.value)} /></label>
          </div>
        </div>

        <div className="border border-white/10 bg-[#121212] p-6 space-y-4">
          <h2 className="display text-xl uppercase tracking-tight font-bold">Your bio</h2>
          <div className="grid grid-cols-2 gap-4">
            <label className="block"><span className={label}>Name</span>
              <input data-testid="content-bio-name-input" className={input} value={c.about.bio_name} onChange={(e) => setAbout("bio_name", e.target.value)} /></label>
            <label className="block"><span className={label}>Role / title</span>
              <input data-testid="content-bio-role-input" className={input} value={c.about.bio_role} onChange={(e) => setAbout("bio_role", e.target.value)} /></label>
          </div>
          <label className="block"><span className={label}>Photo URL (https)</span>
            <input data-testid="content-bio-photo-input" className={input} value={c.about.bio_photo} onChange={(e) => setAbout("bio_photo", e.target.value)} placeholder="https://…" /></label>
          <label className="block"><span className={label}>Bio</span>
            <textarea data-testid="content-bio-text-input" rows={11} className={input} value={c.about.bio_text} onChange={(e) => setAbout("bio_text", e.target.value)} /></label>
        </div>
      </div>

      <h2 className="display text-2xl uppercase tracking-tight font-bold mt-14 flex items-center gap-2">
        <FileText className="w-5 h-5 text-[#00e5ff]" /> Legal pages
      </h2>
      <div className="mt-4 space-y-6">
        {legal.map((s) => (
          <div key={s.key} className="border border-white/10 bg-[#121212] p-6 space-y-4">
            <div>
              <h3 className="display text-lg uppercase tracking-tight font-bold">{s.name}</h3>
              <p className="text-[11px] text-zinc-500 mt-1">{s.note}</p>
            </div>
            <label className="block"><span className={label}>Title</span>
              <input data-testid={`content-${s.key}-title-input`} className={input} value={c[s.key].title} onChange={(e) => setSection(s.key, "title", e.target.value)} /></label>
            <label className="block"><span className={label}>Body (blank line between paragraphs)</span>
              <textarea data-testid={`content-${s.key}-body-input`} rows={9} className={input} value={c[s.key].body} onChange={(e) => setSection(s.key, "body", e.target.value)} /></label>
          </div>
        ))}
      </div>

      <button data-testid="content-save-btn" disabled={busy} onClick={save}
        className="mt-8 bg-[#00e5ff] text-black font-bold uppercase tracking-wide px-7 py-4 flex items-center gap-2 hover:bg-[#00b3cc] disabled:opacity-50 transition-colors duration-200">
        <Save className="w-4 h-4" /> Save all content
      </button>
      <p className="text-[11px] text-zinc-600 mt-3">The legal text is a starting point, not legal advice — have it reviewed before you rely on it.</p>
    </div>
  );
}
